import { Debug } from '../debug';
import {
  doesNotExist,
  isActive,
  parentExists,
  parseArgumentName,
} from '../component-validations/helpers';

const warnedByInstance = new WeakMap();

/**
 * Builds the warning message for an overridden argument.
 * @param {object} options
 * @param {string} options.component
 * @param {string} options.argName
 * @param {object} options.entry
 * @param {object} options.componentInstance
 * @returns {string}
 */
function buildMessage({ component, argName, entry, componentInstance }) {
  const instanceId = componentInstance?.id ? ` [instance: ${componentInstance.id}]` : '';
  const debugHint = `\n\n💡 Debug: Search for <${(component ?? '').replace(/::/g, '/')} @${argName}=...> in templates to find the source.`;

  return `@${argName} argument of ${component} is overridden when @${entry.name} is provided because ${entry.reason}${instanceId}${debugHint}`;
}

/**
 * Default decision for whether an override warning should fire, used when the
 * component does not provide an `isArgumentOverridden` hook. Warns when the
 * overriding arg is active (honoring an optional `when` value match) and the
 * overridden arg itself is active.
 *
 * @param {object} options
 * @param {object} options.args - Component `this.args`
 * @param {string} options.argName - The overridden arg name
 * @param {object} options.entry - The `overriddenBy` entry (`{ name, reason, when? }`)
 * @returns {boolean}
 */
function defaultIsOverridden({ args, argName, entry }) {
  const overridingValue = parseArgumentName(args, entry.name);
  const normalizedWhen = normalizeWhenValue(entry.when);
  const overridingActive =
    normalizedWhen === undefined
      ? isActive(overridingValue)
      : normalizedWhen.includes(overridingValue);

  return overridingActive && isActive(parseArgumentName(args, argName));
}

/**
 * Whether the override applies — delegated to the component hook when present,
 * otherwise the default existence/`when` check.
 * @param {object} options
 * @param {object} options.args
 * @param {string} options.argName
 * @param {object} options.entry
 * @param {Function|null} options.hook
 * @returns {boolean}
 */
function isOverridden({ args, argName, entry, hook }) {
  if (hook) return hook(argName, entry);

  return defaultIsOverridden({ args, argName, entry });
}

/**
 * Normalizes a `when` clause to an array (or undefined when not provided).
 * @param {*} when
 * @returns {Array|undefined}
 */
function normalizeWhenValue(when) {
  if (doesNotExist(when)) return;

  return Array.isArray(when) ? when : [when];
}

/**
 * Builds the stable warning id for an overridden argument.
 * @param {string|null|undefined} component - e.g. `Spds::Core::Container`
 * @param {string} argName
 * @returns {string}
 */
function overrideWarningId(component, argName) {
  const path = (component ?? 'Unknown')
    .split('::')
    .map(part => part.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase())
    .join('.');
  return `spds.argument-override.${path}.${argName.replace(/\./g, '-')}`;
}

/**
 * Resolves the component's `isArgumentOverridden` hook, bound to the instance, or null.
 * @param {object} componentInstance
 * @returns {Function|null}
 */
function resolveOverrideHook(componentInstance) {
  const { isArgumentOverridden } = componentInstance;

  if (typeof isArgumentOverridden !== 'function') return null;

  return isArgumentOverridden.bind(componentInstance);
}

/**
 * Emits the warning for one `overriddenBy` entry when it applies and hasn't fired yet.
 * @param {object} context - Shared `{ args, component, componentInstance, hook, warned }`
 * @param {string} argName
 * @param {object} entry
 */
function warnForEntry(context, argName, entry) {
  const { args, component, componentInstance, hook, warned } = context;
  const key = `${argName}::${entry.name}`;

  if (warned.has(key)) return;
  if (!isOverridden({ args, argName, entry, hook })) return;

  warned.add(key);
  Debug.warn(buildMessage({ component, argName, entry, componentInstance }), false, {
    id: overrideWarningId(component, argName),
  });
}

/**
 * Emits development-only warnings for arguments whose value is silently discarded
 * because of an overriding argument, as declared via `Arg.overriddenBy(...)` in
 * component metadata.
 *
 * Only runs when invoked from the SPDS base `validateArguments` modifier (same coverage
 * as debug argument validation): components must include `{{this.validateArguments}}`
 * in their template for this to run.
 *
 * When the component defines an `isArgumentOverridden(argName, entry)` method, the
 * decision is fully delegated to it (so it can account for runtime/CSS conditions a
 * static `when` cannot express). Otherwise a default existence/`when` check is used.
 *
 * @param {object} options
 * @param {object} options.args - Component `this.args`
 * @param {import('./builder/metadata').Metadata|{ meta?: object, component?: string }} options.metadata
 * @param {object} options.componentInstance - Component instance (dedupe key + optional hook host)
 */
export function warnOverriddenArguments({ args, metadata, componentInstance }) {
  const { component, meta } = metadata;

  if (!(meta && componentInstance)) return;

  const context = {
    args,
    component,
    componentInstance,
    hook: resolveOverrideHook(componentInstance),
    warned: warnedKeysFor(componentInstance),
  };

  for (const [argName, argMeta] of Object.entries(meta)) {
    warnOverridesForArg(context, argName, argMeta?.overriddenBy);
  }
}

/**
 * Emits warnings for a single overridden arg across all of its `overriddenBy` entries.
 * @param {object} context - Shared `{ args, component, componentInstance, hook, warned }`
 * @param {string} argName
 * @param {Array<object>|undefined} entries
 */
function warnOverridesForArg(context, argName, entries) {
  if (!entries?.length) return;
  if (!parentExists(context.args, argName)) return;

  for (const entry of entries) {
    warnForEntry(context, argName, entry);
  }
}

/**
 * Returns the per-instance set of already-warned keys, creating it on first use.
 * @param {object} componentInstance
 * @returns {Set<string>}
 */
function warnedKeysFor(componentInstance) {
  let warned = warnedByInstance.get(componentInstance);

  if (!warned) {
    warned = new Set();
    warnedByInstance.set(componentInstance, warned);
  }

  return warned;
}

export default {
  warnOverriddenArguments,
};
